/**
 * fetch ベースの HTTP クライアント
 *
 * axios（Securify で使用禁止パッケージに登録）の置き換えとして、
 * 本プロジェクトで使用している範囲の axios 互換 API を提供する。
 * - インスタンス生成: createHttpClient({ baseURL, timeout, withCredentials })
 * - 呼び出し: instance(config) / instance.get(url, config)
 * - config: method, url, params, data, headers, responseType("blob" | "arraybuffer" | "text" | "json"), timeout, withCredentials
 * - レスポンス: { data, status, statusText, headers, config, request: { responseURL } }
 * - エラー: HttpError（code: "ECONNABORTED"（タイムアウト）/ "ERR_NETWORK" / "ERR_BAD_REQUEST" / "ERR_BAD_RESPONSE"、
 *   HTTP ステータスエラー時は error.response にレスポンスを格納）
 * - interceptors.response.use(onFulfilled, onRejected)
 */

const DEFAULT_ACCEPT = "application/json, text/plain, */*"

/**
 * HTTP 通信エラー
 */
export class HttpError extends Error {
  constructor(message, code, config, response) {
    super(message)
    this.name = "HttpError"
    this.code = code
    this.config = config
    this.response = response
    this.status = response ? response.status : undefined
  }
}

// クエリパラメータ値のエンコード（axios と同じ規則）
function encodeParam(val) {
  return encodeURIComponent(val)
    .replace(/%3A/gi, ":")
    .replace(/%24/g, "$")
    .replace(/%2C/gi, ",")
    .replace(/%20/g, "+")
}

function convertParamValue(value) {
  if (value === null) return ""
  if (value instanceof Date) return value.toISOString()
  if (typeof value === "object") return JSON.stringify(value)
  return String(value)
}

// クエリパラメータの直列化（undefined は除外、null は空文字、配列は key[]=値）
function serializeParams(params) {
  if (params instanceof URLSearchParams) {
    return params.toString()
  }
  const pairs = []
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value === undefined) return
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v === undefined || v === null) return
        pairs.push(`${encodeParam(`${key}[]`)}=${encodeParam(convertParamValue(v))}`)
      })
      return
    }
    pairs.push(`${encodeParam(key)}=${encodeParam(convertParamValue(value))}`)
  })
  return pairs.join("&")
}

function isAbsoluteURL(url) {
  return /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)
}

// baseURL と url の結合（url が絶対 URL の場合は baseURL を使わない）
function buildURL(baseURL, url, params) {
  let fullURL = url || ""
  if (baseURL && !isAbsoluteURL(fullURL)) {
    fullURL = fullURL
      ? baseURL.replace(/\/?\/$/, "") + "/" + fullURL.replace(/^\/+/, "")
      : baseURL
  }
  if (params) {
    const serialized = serializeParams(params)
    if (serialized) {
      const hashIndex = fullURL.indexOf("#")
      if (hashIndex !== -1) {
        fullURL = fullURL.slice(0, hashIndex)
      }
      fullURL += (fullURL.indexOf("?") === -1 ? "?" : "&") + serialized
    }
  }
  return fullURL
}

// リクエストボディの変換（axios の既定の変換に準拠）
function transformRequestBody(data, headers) {
  if (data === undefined || data === null) {
    headers.delete("Content-Type")
    return undefined
  }
  if (data instanceof FormData) {
    // boundary 付きの Content-Type はブラウザに設定させる
    headers.delete("Content-Type")
    return data
  }
  if (
    data instanceof Blob ||
    data instanceof ArrayBuffer ||
    data instanceof ReadableStream
  ) {
    return data
  }
  if (ArrayBuffer.isView(data)) {
    return data.buffer
  }
  if (data instanceof URLSearchParams) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/x-www-form-urlencoded;charset=utf-8")
    }
    return data.toString()
  }
  const contentType = headers.get("Content-Type") || ""
  if (typeof data === "object" || contentType.indexOf("application/json") > -1) {
    if (!headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json")
    }
    return typeof data === "string" ? data : JSON.stringify(data)
  }
  return data
}

// レスポンスボディの変換
async function transformResponseBody(res, responseType) {
  if (responseType === "blob") {
    return await res.blob()
  }
  if (responseType === "arraybuffer") {
    return await res.arrayBuffer()
  }
  const text = await res.text()
  if (responseType === "text" || !text) {
    return text
  }
  try {
    return JSON.parse(text)
  } catch (e) {
    if (responseType === "json") {
      throw e
    }
    // JSON でなければ文字列のまま返す
    return text
  }
}

async function dispatchRequest(config) {
  const method = (config.method || "get").toUpperCase()
  const headers = new Headers({ Accept: DEFAULT_ACCEPT })
  Object.entries(config.headers || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      headers.set(key, value)
    }
  })
  let body = transformRequestBody(config.data, headers)
  if (method === "GET" || method === "HEAD") {
    body = undefined
  }

  const url = buildURL(config.baseURL, config.url, config.params)
  const controller = new AbortController()
  let timedOut = false
  const timer =
    config.timeout > 0
      ? setTimeout(() => {
          timedOut = true
          controller.abort()
        }, config.timeout)
      : null

  const toTransportError = (e) => {
    if (e instanceof HttpError) return e
    if (timedOut) {
      return new HttpError(`timeout of ${config.timeout}ms exceeded`, "ECONNABORTED", config)
    }
    return new HttpError(e && e.message ? e.message : "Network Error", "ERR_NETWORK", config)
  }

  try {
    let res
    try {
      res = await fetch(url, {
        method,
        headers,
        body,
        credentials: config.withCredentials ? "include" : "same-origin",
        signal: controller.signal,
      })
    } catch (e) {
      throw toTransportError(e)
    }

    let data
    try {
      data = await transformResponseBody(res, config.responseType)
    } catch (e) {
      if (timedOut || e.name === "AbortError" || e instanceof TypeError) {
        throw toTransportError(e)
      }
      throw new HttpError(e.message, "ERR_BAD_RESPONSE", config)
    }

    const response = {
      data,
      status: res.status,
      statusText: res.statusText,
      headers: Object.fromEntries(res.headers.entries()),
      config,
      request: { responseURL: res.url },
    }

    if (res.status < 200 || res.status >= 300) {
      throw new HttpError(
        `Request failed with status code ${res.status}`,
        res.status >= 500 ? "ERR_BAD_RESPONSE" : "ERR_BAD_REQUEST",
        config,
        response
      )
    }
    return response
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * HTTP クライアントのインスタンスを生成する
 * @param {Object} defaults 既定の設定（baseURL, timeout, withCredentials など）
 * @returns 呼び出し可能なインスタンス
 */
export function createHttpClient(defaults = {}) {
  const responseInterceptors = []

  function request(config = {}) {
    const merged = {
      ...defaults,
      ...config,
      headers: { ...(defaults.headers || {}), ...(config.headers || {}) },
    }
    let promise = dispatchRequest(merged)
    responseInterceptors.forEach(({ onFulfilled, onRejected }) => {
      promise = promise.then(onFulfilled, onRejected)
    })
    return promise
  }

  const instance = (config) => request(config)
  instance.request = request
  instance.get = (url, config = {}) => request({ ...config, url, method: "get" })
  instance.interceptors = {
    response: {
      use(onFulfilled, onRejected) {
        responseInterceptors.push({ onFulfilled, onRejected })
      },
    },
  }
  return instance
}

/**
 * 既定のインスタンス（baseURL なし）
 */
const httpClient = createHttpClient()

export default httpClient
